/**
 * Aktiv prüfen, ob die wichtigsten Frontend-Assets vom Browser tatsächlich
 * geladen werden können. Unterscheidet zwischen Vite-Dev und Production:
 *
 *  - Production: zieht /assets/*.js + /assets/*.css aus den vorhandenen
 *    <script>- und <link>-Tags und probiert sie per HEAD-Fetch nach.
 *  - Dev: prüft /@vite/client und den Haupt-Module-Eintrag /src/main.tsx
 *    bzw. /src/entry-client.tsx, falls vorhanden.
 *
 * Liefert ein detailliertes Resultat, damit der Aufrufer entscheiden kann,
 * ob die Beobachtung reproduzierbar ist.
 */
export type AssetProbeResult = {
  url: string;
  ok: boolean;
  status: number | null;
  error: string | null;
  durationMs: number;
};

export type AssetSelfTestResult = {
  capturedAt: string;
  mode: "dev" | "prod" | "unknown";
  probes: AssetProbeResult[];
  failedCount: number;
  totalCount: number;
  // true wenn jede einzelne Probe fehlschlug → klares Indiz für AdBlock /
  // Netzwerk-Blockade. Einzelne Fehler werden nicht als kritisch gewertet.
  allFailed: boolean;
};

const PROBE_TIMEOUT_MS = 3000;

function collectProbeUrls(): { mode: AssetSelfTestResult["mode"]; urls: string[] } {
  if (typeof document === "undefined") return { mode: "unknown", urls: [] };

  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]")).map(
    (s) => s.src,
  );
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>("link[href]"));

  const prodJs = scripts.filter((src) => /\/assets\/.*\.js/.test(src));
  const prodCss = links
    .filter((l) => l.rel === "stylesheet" && /\/assets\/.*\.css/.test(l.href))
    .map((l) => l.href);
  const prodPreload = links
    .filter((l) => l.rel === "modulepreload" && /\/assets\/.*\.js/.test(l.href))
    .map((l) => l.href);

  if (prodJs.length > 0 || prodCss.length > 0 || prodPreload.length > 0) {
    // Höchstens je 2 Stichproben, damit der Test schnell bleibt.
    return {
      mode: "prod",
      urls: dedupe([...prodJs.slice(0, 2), ...prodCss.slice(0, 2), ...prodPreload.slice(0, 1)]),
    };
  }

  const viteClient = scripts.find((s) => /\/@vite\/client/.test(s));
  const viteMain = scripts.find((s) => /\/src\/(main|entry-client)\.tsx/.test(s));
  if (viteClient || viteMain) {
    return {
      mode: "dev",
      urls: dedupe([viteClient, viteMain].filter(Boolean) as string[]),
    };
  }

  return { mode: "unknown", urls: [] };
}

function dedupe<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

async function probe(url: string): Promise<AssetProbeResult> {
  const started = performance.now();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    // Wir nutzen GET (HEAD wird von vielen Static-Hosts/CDNs mit 405
    // beantwortet) und no-cors, damit der Browser auch ohne CORS-Header
    // antwortet. Entscheidend ist NUR, ob die Anfrage durchkam — eine
    // HTTP-Antwort (auch 404/405) bedeutet, der Server wurde erreicht
    // und ist damit nicht durch einen AdBlocker geblockt. Nur eine
    // geworfene Exception zählt als "geblockt".
    const res = await fetch(url, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    return {
      url,
      ok: true,
      status: res.status || (res.type === "opaque" ? 0 : null),
      error: null,
      durationMs: performance.now() - started,
    };
  } catch (err) {
    return {
      url,
      ok: false,
      status: null,
      error: err instanceof Error ? err.message : "fetch failed",
      durationMs: performance.now() - started,
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function runAssetSelfTest(): Promise<AssetSelfTestResult> {
  const { mode, urls } = collectProbeUrls();
  if (urls.length === 0) {
    return {
      capturedAt: new Date().toISOString(),
      mode,
      probes: [],
      failedCount: 0,
      totalCount: 0,
      allFailed: false,
    };
  }
  const probes = await Promise.all(urls.map(probe));
  const failedCount = probes.filter((p) => !p.ok).length;
  return {
    capturedAt: new Date().toISOString(),
    mode,
    probes,
    failedCount,
    totalCount: probes.length,
    allFailed: probes.length > 0 && failedCount === probes.length,
  };
}
